import { createRouter, createWebHistory } from "vue-router"

import Cookies from "js-cookie"

import { eventBus } from "@/plugins/eventBus.js"

const ViewLogin = () => import("@/views/ViewLogin.vue")
const ViewLoginWithCode = () => import("@/views/ViewLoginWithCode.vue")
const ViewSignup = () => import("@/views/ViewSignup.vue")
const ViewPaywall = () => import("@/views/ViewPaywall.vue")
const ViewForgotPassword = () => import("@/views/ViewForgotPassword.vue")
const ViewNotFound = () => import("@/views/ViewNotFound.vue")
const ViewEmailConfirmation = () => import("@/views/ViewEmailConfirmation.vue")
const ViewResendEmailConfirmation = () => import("@/views/ViewResendEmailConfirmation.vue")
const ViewChangePassword = () => import("@/views/ViewChangePassword.vue")
const ViewConsolePreview = () => import("@/views/ViewConsolePreview.vue")

const ViewGroups = () => import("@/views/ViewGroups.vue")
const ViewGroupsRequests = () => import("@/views/ViewGroupsRequests.vue")
const ViewHashtags = () => import("@/views/ViewHashtags.vue")
const ViewSearch = () => import("@/views/ViewSearch.vue")
const ViewEvent = () => import("@/views/ViewEvent.vue")
const ViewEvents = () => import("@/views/ViewEvents.vue")
const ViewFolder = () => import("@/views/ViewFolder.vue")
const ViewArticle = () => import("@/views/ViewArticle.vue")
const ViewProfile = () => import("@/views/ViewProfile.vue")
const ViewProfileEdit = () => import("@/views/ViewProfileEdit.vue")
const ViewProfileSettings = () => import("@/views/ViewProfileSettings.vue")
const ViewEmailsSettings = () => import("@/views/ViewEmailsSettings.vue")
const ViewLivestreamingSettings = () => import("@/views/ViewLivestreamingSettings.vue")
const ViewCookieSettings = () => import("@/views/ViewCookieSettings.vue")
const ViewPushNotificationsSettings = () => import("@/views/ViewPushNotificationsSettings.vue")
const ViewNotificationsCentreSettings = () => import("@/views/ViewNotificationsCentreSettings.vue")
const ViewDeleteAccount = () => import("@/views/ViewDeleteAccount.vue")
const ViewMessages = () => import("@/views/ViewMessages.vue")
const ViewRtmp = () => import("@/views/ViewRtmp.vue")
const ViewLive = () => import("@/views/ViewLive.vue")
const ViewScheduledPosts = () => import("@/views/ViewScheduledPosts.vue")
const ViewMemberDirectory = () => import("@/views/ViewMemberDirectory.vue")
const ViewWalkthrough = () => import("@/views/ViewWalkthrough.vue")
const ViewCourse = () => import("@/views/ViewCourse.vue")
const ViewCourses = () => import("@/views/ViewCourses.vue")
const ViewLesson = () => import("@/views/ViewLesson.vue")
const ViewBlockedMembers = () => import("@/views/ViewBlockedMembers.vue")

const ViewPost = () => import("@/views/ViewPost.vue")
const ViewThirdPartySoftware = () => import("@/views/ViewThirdPartySoftware.vue")

import { useConfigurationStore } from "@/store/configuration.js"
import { useUserSessionStore } from "@/store/userSession.js"
import { useAppStateStore } from "@/store/appState.js"

import routes from "@/routes.js"

import * as pageMetaService from "@/services/pageMeta.js"
import * as paywallService from "@/services/paywallService.js"
import * as sentryReporter from "@/services/sentryReporter.js"

import * as subscriptionsApi from "@/api/subscriptionsApi"

export const sessionRoutes = [routes.SIGNUP, routes.LOGIN, routes.LOGIN_SSO, routes.LOGIN_PASSWORD, routes.LOGIN_CODE]
const nonAuthRoutes = [
  ...sessionRoutes,
  routes.FORGOT_PASSWORD,
  routes.EMAIL_CONFIRMATION,
  routes.RESEND_EMAIL_CONFIRMATION,
  routes.CHANGE_PASSWORD,
  routes.POST,
  routes.STYLE_GUIDE,
  routes.CONSOLE_PREVIEW
]

const state = {
  authRedirect: null,
  previous: {
    name: null
  },
  history: [],
  wasMagicLinkRequestForAuthenticatedUser: false,
  wasSubscriptionSuccess: false,
  pnId: null,
  isRoot: false
}

export { routes }
export const getRouterState = () => state
export const setRouterAuthRedirect = (route) => (state.authRedirect = route?.fullPath)

export const routerConfig = {
  history: createWebHistory(Cookies.get("X-Secret") && Cookies.get("X-Secret-Id") ? "/webview/" : "/"),
  scrollBehavior(to, from) {
    if (to.name !== from.name && to.meta?.keepScroll) {
      return false
    }
    return { top: 0 }
  },
  routes: [
    {
      path: "/",
      name: routes.ROOT,
      redirect() {
        const configurationStore = useConfigurationStore()
        const userSessionStore = useUserSessionStore()

        return userSessionStore.isLoggedIn ? configurationStore.configuration.homePageRoute : { name: routes.LOGIN }
      }
    },
    {
      path: "/console-preview",
      name: routes.CONSOLE_PREVIEW,
      components: {
        nonAuth: ViewConsolePreview
      }
    },
    {
      path: "/groups/:groupId?/requests",
      name: routes.GROUPS_REQUESTS,
      components: {
        auth: ViewGroupsRequests
      }
    },
    {
      path: "/groups/:groupId?/:sort?",
      name: routes.GROUPS,
      components: {
        auth: ViewGroups
      },
      meta: {
        keepScroll: true
      }
    },
    {
      path: "/hashtag/:hashtagId",
      name: routes.HASHTAGS,
      components: {
        auth: ViewHashtags
      }
    },
    {
      path: "/posts/:postId",
      name: routes.POST,
      components: {
        auth: ViewPost,
        nonAuth: ViewPost
      }
    },
    {
      path: "/posts/:postId/comments/:commentId",
      name: routes.COMMENT,
      components: {
        auth: ViewPost,
        nonAuth: ViewPost
      }
    },
    {
      path: "/login/callback",
      name: routes.LOGIN_SSO,
      components: {
        nonAuth: ViewLogin
      }
    },
    {
      path: "/login/password",
      name: routes.LOGIN_PASSWORD,
      components: {
        nonAuth: ViewLogin
      }
    },
    {
      path: "/login/:referralCode?",
      name: routes.LOGIN,
      components: {
        nonAuth: ViewLogin
      }
    },
    {
      path: "/magic/:referralCode?",
      name: routes.LOGIN_CODE,
      components: {
        nonAuth: ViewLoginWithCode
      }
    },
    {
      path: "/signup/:referralCode?",
      name: routes.SIGNUP,
      components: {
        nonAuth: ViewSignup
      }
    },
    {
      //The token is passed as a hash to avoid logging it in the server logs
      path: "/change-password",
      name: routes.CHANGE_PASSWORD,
      components: {
        nonAuth: ViewChangePassword,
        auth: ViewChangePassword
      }
    },
    {
      path: "/paywall/:requiredPlanName?",
      name: routes.PAYWALL,
      components: {
        auth: ViewPaywall
      }
    },
    {
      path: "/profile/edit",
      name: routes.PROFILE_EDIT,
      components: {
        auth: ViewProfileEdit
      }
    },
    {
      path: "/profile/settings",
      name: routes.PROFILE_SETTINGS,
      components: {
        auth: ViewProfileSettings
      }
    },
    {
      path: "/profile/settings/emails",
      name: routes.PROFILE_SETTINGS_EMAILS,
      components: {
        auth: ViewEmailsSettings
      }
    },
    {
      path: "/profile/settings/livestreaming",
      name: routes.PROFILE_SETTINGS_LIVESTREAMING,
      components: {
        auth: ViewLivestreamingSettings
      }
    },
    {
      path: "/profile/settings/cookie-preferences",
      name: routes.PROFILE_SETTINGS_COOKIES,
      components: {
        auth: ViewCookieSettings
      }
    },
    {
      path: "/profile/settings/push-notifications",
      name: routes.PROFILE_SETTINGS_PUSH_NOTIFICATIONS,
      components: {
        auth: ViewPushNotificationsSettings
      }
    },
    {
      path: "/profile/settings/notifications-centre",
      name: routes.PROFILE_SETTINGS_NOTIFICATIONS_CENTRE,
      components: {
        auth: ViewNotificationsCentreSettings
      }
    },
    {
      path: "/profile/settings/delete-account",
      name: routes.DELETE_ACCOUNT,
      components: {
        auth: ViewDeleteAccount
      }
    },
    {
      path: "/profile/:id(\\d+)?",
      name: routes.PROFILE,
      components: {
        auth: ViewProfile
      },
      meta: {
        keepScroll: true
      }
    },
    {
      path: "/profile/:id(\\d+)/badges/:badgeKey",
      name: routes.PROFILE_BADGE,
      components: {
        auth: ViewProfile
      },
      meta: {
        keepScroll: true
      }
    },
    {
      path: "/search/:mode?",
      name: routes.SEARCH,
      components: {
        auth: ViewSearch
      },
      meta: {
        keepScroll: true
      }
    },
    {
      path: "/folders/:folderId?",
      name: routes.FOLDER,
      components: {
        auth: ViewFolder
      }
    },
    {
      path: "/articles/:articleId",
      name: routes.ARTICLE,
      components: {
        auth: ViewArticle
      }
    },
    {
      path: "/events",
      name: routes.EVENTS,
      components: {
        auth: ViewEvents
      }
    },
    {
      path: "/events/:id",
      name: routes.EVENT,
      components: {
        auth: ViewEvent
      }
    },
    {
      path: "/forgot-password",
      name: routes.FORGOT_PASSWORD,
      components: {
        nonAuth: ViewForgotPassword
      }
    },
    {
      path: "/email-confirmation/:token",
      name: routes.EMAIL_CONFIRMATION,
      components: {
        auth: ViewEmailConfirmation,
        nonAuth: ViewEmailConfirmation
      }
    },
    {
      path: "/resend-email-confirmation",
      name: routes.RESEND_EMAIL_CONFIRMATION,
      components: {
        auth: ViewResendEmailConfirmation,
        nonAuth: ViewResendEmailConfirmation
      }
    },
    {
      path: "/messages/:id(\\d+)?/:mode?",
      name: routes.MESSAGES,
      components: {
        auth: ViewMessages
      }
    },
    {
      path: "/live/rtmp/:groupId/:eventId?",
      name: routes.RTMP,
      components: {
        auth: ViewRtmp
      }
    },
    {
      path: "/live/broadcast/:groupId/:eventId?",
      name: routes.BROADCAST,
      components: {
        auth: ViewLive
      }
    },
    {
      path: "/live/:id?",
      name: routes.LIVE,
      components: {
        auth: ViewLive
      }
    },
    {
      path: "/scheduled-posts",
      name: routes.SCHEDULED_POSTS,
      components: {
        auth: ViewScheduledPosts
      }
    },
    {
      path: "/members",
      name: routes.MEMBER_DIRECTORY,
      components: {
        auth: ViewMemberDirectory
      },
      meta: {
        keepScroll: true
      }
    },
    {
      path: "/third-party-software",
      name: routes.THIRD_PARTY_SOFTWARE,
      components: {
        auth: ViewThirdPartySoftware
      }
    },
    {
      path: "/walkthrough/:slideId?",
      name: routes.WALKTHROUGH,
      components: {
        auth: ViewWalkthrough
      }
    },
    {
      path: "/courses/:courseId(\\d+)",
      name: routes.COURSE,
      components: {
        auth: ViewCourse
      },
      meta: {
        keepScroll: true
      }
    },
    {
      path: "/courses/:courseId/lessons/:lessonId",
      name: routes.LESSON,
      components: {
        auth: ViewLesson
      }
    },
    {
      path: "/courses/:tab?",
      name: routes.COURSES,
      components: {
        auth: ViewCourses
      }
    },
    {
      path: "/blocked-members",
      name: routes.BLOCKED_MEMBERS,
      components: {
        auth: ViewBlockedMembers
      }
    },
    {
      path: "/:pathMatch(.*)*",
      name: routes._404,
      components: {
        auth: ViewNotFound,
        nonAuth: ViewNotFound
      }
    }
  ]
}

const router = createRouter(routerConfig)

const redirectLoggedInUser = (next, to) => {
  //We force user redirection, clear auth redirect to avoid infinite loop
  state.authRedirect = null
  next(to)
}

const cancelSubscriptionProcess = async () => {
  try {
    const appStateStore = useAppStateStore()
    appStateStore.setSubscriptionIdInProgress(null)
    await subscriptionsApi.completePaymentTicket()
  } catch (err) {
    sentryReporter.reportError(err)
  }
}

const historyLimit = 20

export const beforeEach = async (to, from, next) => {
  pageMetaService.setPageTitle()
  pageMetaService.setPageDescription()

  const configurationStore = useConfigurationStore()
  const userSessionStore = useUserSessionStore()
  const appStateStore = useAppStateStore()

  const configuration = configurationStore.configuration
  const isLoggedIn = userSessionStore.isLoggedIn
  const user = userSessionStore.user
  const walkthroughCompleted = userSessionStore.walkthroughCompleted
  const walkthroughSkipped = appStateStore.walkthroughSkipped
  const ownedPaywallPlan = userSessionStore.ownedPaywallPlan
  const webViewMode = appStateStore.webViewMode

  //Don't allow webview to navigate
  if (webViewMode && from.name) {
    //Leave console.error here for mobile devs
    console.error("Webview navigation prevented")
    sentryReporter.reportError(new Error("Webview navigation prevented"))
    return
  }

  if (to.query.subscription_success === "true") {
    sentryReporter.reportMessage("[STRIPE] subscription_success", { key: "paywallState", value: "stripe_purchased" })
    await cancelSubscriptionProcess()
    // Go forward, if it's possible - don't let users to go back to Stripe checkout page
    // https://disciple.kanbanize.com/ctrl_board/52/cards/49817/details/
    window.history.forward()
    state.wasSubscriptionSuccess = true
    next({ ...paywallService.getRouteAfterSubscriptionSuccess() })
    return
  }

  if (to.query.subscription_cancelled === "true") {
    sentryReporter.reportMessage("[STRIPE] subscription_cancelled", { key: "paywallState", value: "stripe_cancelled" })
    await cancelSubscriptionProcess()

    delete to.query.subscription_cancelled
    next(to)
    return
  }

  if (to.query.pn) {
    state.pnId = to.query.pn
    delete to.query.pn
    next(to)
    return
  }

  const subscriptionRequired = configuration.isPremium && !ownedPaywallPlan

  state.isRoot = isOnRootPage(to, configuration.homePageRoute)

  if (isLoggedIn && [routes.LOGIN, routes.LOGIN_PASSWORD, routes.SIGNUP, routes.FORGOT_PASSWORD].includes(to.name)) {
    if (to.path.includes("magic") && to.hash) {
      state.wasMagicLinkRequestForAuthenticatedUser = true
    }

    redirectLoggedInUser(next, { name: routes.ROOT })
    return
  }

  if (to.name !== routes.PAYWALL) {
    appStateStore.setLastPaywallSource(null)

    if (isLoggedIn && subscriptionRequired && to.name !== routes.DELETE_ACCOUNT && to.name !== routes.PROFILE_SETTINGS) {
      redirectLoggedInUser(next, { name: routes.PAYWALL })
      return
    }
  }

  if (isLoggedIn && !subscriptionRequired && !user.onboarding_completed) {
    if (to.name === routes.PROFILE_EDIT) {
      //Do not clear auth redirect, it will be used after onboarding
      next()
    } else {
      // Don't save paywall route to be redirected to, important for premium apps when someone registers, lands on paywall,
      // gets subscription via CMS and refreshes the paywall page, landing on onboarding
      // https://disciple.kanbanize.com/ctrl_board/58/cards/53458/details/
      if (to.name !== routes.PAYWALL) {
        state.authRedirect = to.fullPath
      }
      next({ name: routes.PROFILE_EDIT })
    }
    return
  }

  if (
    isLoggedIn &&
    !subscriptionRequired &&
    configuration.walkthrough.enabled &&
    !walkthroughCompleted &&
    !walkthroughSkipped &&
    to.name !== routes.WALKTHROUGH
  ) {
    redirectLoggedInUser(next, { name: routes.WALKTHROUGH })
    return
  }

  if (isLoggedIn && state.authRedirect !== null) {
    if (to.fullPath !== state.authRedirect) {
      redirectLoggedInUser(next, { path: state.authRedirect })
      return
    }
    state.authRedirect = null
  }

  if (!isLoggedIn && !nonAuthRoutes.includes(to.name)) {
    state.authRedirect = to.fullPath
    next({ name: routes.LOGIN })
    return
  }

  if (state.wasHistoryBack && state.history.length > 0) {
    state.history.pop()
    state.wasHistoryBack = false
  } else {
    state.history.push(from)
  }

  if (state.history.length > historyLimit) {
    state.history.splice(0, state.history.length - historyLimit)
  }

  state.previous = state.history[state.history.length - 1] || { name: null }

  next()
}

const isOnRootPage = ({ name, params } = {}, homePageRoute) => {
  switch (homePageRoute.name) {
    case routes.FOLDER: {
      return name === routes.FOLDER && ((!params.folderId && !homePageRoute.params?.folderId) || Number(params.folderId) === homePageRoute.params?.folderId)
    }
    case routes.GROUPS: {
      return name === routes.GROUPS && (params?.groupId === homePageRoute.params?.groupId || (!params.groupId && !homePageRoute.params?.groupId))
    }
    default:
      return false
  }
}

router.beforeEach(beforeEach)

const defaultRoute = { name: routes.ROOT }

export const historyBack = async ({ fallbackRoute = defaultRoute } = { fallbackRoute: defaultRoute }) => {
  if (state.history.length <= 1) {
    router.push(fallbackRoute)
    return
  }

  state.wasHistoryBack = true
  await router.push(state.previous)
  eventBus.$emit("router-history-back")
}

router.historyBack = historyBack

export const failedModuleLoadMessages = [
  "Importing a module script failed",
  "Failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "Load failed"
]

export default router
